import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { BBResponseContainer } from '../common-services/response-container';
import { Observable, map, timeout } from 'rxjs';
import { GenericPlateUpdateDTO } from '../dto/genericPlateUpdateDTO';
import { GenericMultiPlateUpdateDTO } from '../dto/genericMultiPlateUpdateDTO';
import { GenericFetchWashAndStainDTO } from '../dto/genericFetchWashAndStainDTO';
import { Sample } from '../models-new/sample';
import { Plate } from '../models-new/plate';
import {Batch} from "../models-new/batch";
import {RemoveSelectedPlateFromBatch} from "../dto/remove-selected-plate-from-batch";
import { SingleRedoMultiPlateRequestDTO } from '../ma-result-evaluation/model/upload-single-redo-file-response-dto';
import { MultiPlateUpdate } from '../ma-plate-preparation/multiplate-update-dto';
import { PlateListDTO } from '../dto/plateListDTO';

@Injectable({
	providedIn: 'root'
})
export class PlateProcessingService {

	SAMPLE_PROCESSING_API = environment.BB_SAMPLE_PROCESSING_API;

	constructor(private httpClient: HttpClient) { }

	getPlates(plateListDTO: PlateListDTO): Observable<BBResponseContainer> {
		const url = this.SAMPLE_PROCESSING_API + 'plate';
		return this.httpClient.put<BBResponseContainer>(url, plateListDTO).pipe(timeout(environment.defaultTimeout));
	}

	getPlateDetailsForMultiUpdate(lab: string, plateID: string, plateType: string): Observable<BBResponseContainer> {
		let url = this.SAMPLE_PROCESSING_API + 'processDetailsbyPlateId/' + lab + '/' + plateID + '?platetype=' + plateType;
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
	}

	getArraysBySourcePlate(lab: string, plateId: string): Observable<Plate[]> {
		const url = `${this.SAMPLE_PROCESSING_API}arraysByPlate/${lab}/${plateId}`;
		return this.httpClient.get<BBResponseContainer>(url).pipe(map(result => result.data as Plate[]),timeout(environment.defaultTimeout));
	}

	checkDuplicatePlateId(labId: string, plateId: string, type: string): Observable<BBResponseContainer> {
		let url = this.SAMPLE_PROCESSING_API + 'checkDuplicatePlateId/' + labId + "/" + plateId + "/" + type;
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
	}

	genericPlateUpdate(genericPlateUpdate: GenericPlateUpdateDTO): Observable<BBResponseContainer> {
		let url = this.SAMPLE_PROCESSING_API + "generic/plate/update";
		return this.httpClient.put<BBResponseContainer>(url, genericPlateUpdate).pipe(timeout(environment.defaultTimeout));
	}

	genericMultiPlateUpdate(genericMultiPlateUpdate: GenericMultiPlateUpdateDTO): Observable<BBResponseContainer> {
		let url = this.SAMPLE_PROCESSING_API + "generic/plates/update/multi";
		return this.httpClient.put<BBResponseContainer>(url, genericMultiPlateUpdate).pipe(timeout(environment.defaultTimeout));
	}

	createNewPlate(plate: Plate): Observable<BBResponseContainer> {
		let url = this.SAMPLE_PROCESSING_API + "plate";
		return this.httpClient.post<BBResponseContainer>(url, plate).pipe(timeout(environment.defaultTimeout));
	}

	genericFetchWashAndStainPrepArray(genericFetchWashAndStainDTO: GenericFetchWashAndStainDTO): Observable<BBResponseContainer> {
		let url = this.SAMPLE_PROCESSING_API + "generic/arrays/fetch/washAndStain";
		return this.httpClient.put<BBResponseContainer>(url, genericFetchWashAndStainDTO).pipe(timeout(environment.defaultTimeout));
	}

	checkSamplePresentInPlate( labId: string,sampleid: string) {
		let url = this.SAMPLE_PROCESSING_API+'checkSamplePresent/'+labId+"/"+sampleid;
		return this.httpClient.get<BBResponseContainer>(url).pipe(map(result => result.data));
	}

	updateReProcessedToSample(labId: string, sampleid: string) {
		let url = this.SAMPLE_PROCESSING_API+'updateReProcessedToSample/'+labId+"/"+sampleid;
		return this.httpClient.put<any>(url, sampleid);
	}

	removeSamples(body){
		let url = this.SAMPLE_PROCESSING_API + "removeSample";
		return this.httpClient.post<any>(url, body);
	  }
	addSampleToPlate(labId: string, plateId: string, sample: Sample) {
		let url = this.SAMPLE_PROCESSING_API + 'addSampleToPlate/' + labId + '/' + plateId;
		return this.httpClient.put<BBResponseContainer>(url, sample).pipe((map(response => response.data as Plate))).pipe(timeout(environment.defaultTimeout));
	}

	getPlate(labId: string, plateId: string) {
		let url = this.SAMPLE_PROCESSING_API + `plate/${labId}/${plateId}`;
		return this.httpClient.get<BBResponseContainer>(url).pipe((map(response => response.data as Plate))).pipe(timeout(environment.defaultTimeout));
	}

	getBatchId(labId: string, prefix: string, type: string) {
		const url = `${this.SAMPLE_PROCESSING_API}getBatchId/${labId}/${prefix}/${type}`;
		return this.httpClient.get<BBResponseContainer>(url).pipe(timeout(environment.defaultTimeout));
	}

	getBatch(labId: string, batchId: string){
		const url = `${this.SAMPLE_PROCESSING_API}getBatch/${labId}/${batchId}`;
		return this.httpClient.get<BBResponseContainer>(url).pipe(map(result => result.data as Batch),timeout(environment.defaultTimeout));
	}

	checkSamplePresentInPlateType(labId: string, sampleid: string, type: string) {
		let url = this.SAMPLE_PROCESSING_API + 'checkSamplePresentInPlateType/' + labId + "/" + sampleid + "/" + type;
		return this.httpClient.get<BBResponseContainer>(url).pipe(map(result => result.data)).pipe(timeout(environment.defaultTimeout));
	}

	uploadLynxWorkfile(formDataFile: FormData):Observable<number>{
		const url = this.SAMPLE_PROCESSING_API + 'uploadLynxWorkfile';
		return this.httpClient.post<BBResponseContainer>(url,formDataFile).pipe(map(result => result.data as number))
	}

	uploadSingleRedoFile(formData: FormData): Observable<BBResponseContainer> {
		const url = this.SAMPLE_PROCESSING_API + "singleRedo/upload";
		return this.httpClient.post<BBResponseContainer>(url, formData)
	}

	updateMultiPlateSingleRedoSamples(data: SingleRedoMultiPlateRequestDTO) {
		const url = this.SAMPLE_PROCESSING_API + "singleRedo/multiplate";
		return this.httpClient.put<BBResponseContainer>(url, data)
	}

	processDetailsbyPlateId(lab:string, plateID: string, plateType: string){
		const url = `${this.SAMPLE_PROCESSING_API}processDetailsbyPlateId/${lab}/${plateID}?platetype=${plateType}`;
		return this.httpClient.get<BBResponseContainer>(url).pipe(map(result => result.data as Plate))
	}

	uploadPrecellLogfileV2(formDataFile: FormData, labId: string){
		const url = `${this.SAMPLE_PROCESSING_API}uploadPrecellLogFile/v2/${labId}`;
		return this.httpClient.post<any>(url,formDataFile);
	}

    getPlatesBasedOnBatch(labID:string,batchID:string): Observable<Plate[]>{
        const url = `${this.SAMPLE_PROCESSING_API}batch/${labID}/${batchID}`;
        return this.httpClient.get<BBResponseContainer>(url).pipe(map(result => result.data as Plate[]),timeout(environment.defaultTimeout));
    }

	removeSelectedPlatesFromBatch(body:RemoveSelectedPlateFromBatch): Observable<string>{
		const url = `${this.SAMPLE_PROCESSING_API}removeSelectedPlatesFromBatch`;
		return this.httpClient.post<BBResponseContainer>(url,body).pipe(map(result => result.data as string),timeout(environment.defaultTimeout));
	}

	updatePlateSelectedStatusInNewUi(data: any) {
		const url = `${this.SAMPLE_PROCESSING_API}admin/update/plateStatus/v2`;
		return this.httpClient.put<BBResponseContainer>(url, data).pipe(map(result => result.data))
	}

	updateQCCheckForMultiplePlates(multiPlateUpdate: MultiPlateUpdate, labid: string) {
		const url = `${this.SAMPLE_PROCESSING_API}updateMultiplePlatesQCCheck/${labid}`;
		return this.httpClient.put<BBResponseContainer>(url, multiPlateUpdate).pipe(map(result => result.data as string))
	}

	updatePlateProcessflowname(data: any) {
		const url = `${this.SAMPLE_PROCESSING_API}admin/update/plateProcessflow'`;
		return this.httpClient.put<BBResponseContainer>(url, data).pipe((map(result => result.data)))
	}

	createPlateBarcode(labId: string, projectName:string, year: string, barcodeFormat: string): Observable<BBResponseContainer> {
		const url = `${this.SAMPLE_PROCESSING_API}generic/createBarcode`;
		const requestParam = new HttpParams().set('labId', labId).set('projectName',projectName)
							.set("year",year).set("barcodeFormat",barcodeFormat);
		const options = url ? { params: requestParam } : {};
		return this.httpClient.get<BBResponseContainer>(url,options).pipe(timeout(environment.defaultTimeout));
	}

	  updateNewPlateID(selectedLabId: string, duplicatePlateID: string, newPlateID: string) {
		const url = this.SAMPLE_PROCESSING_API + 'run/update/newPlateID';
		const data = {
		  labId: selectedLabId,
		  duplicatePlateID: duplicatePlateID,
		  newPlateID: newPlateID
		}
		return this.httpClient.put<BBResponseContainer>(url, data).pipe(map(result => result.data as string),timeout(environment.defaultTimeout));
	  }
}
